import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";

import { colleges } from "../data/colleges";
import { useReveal } from "../lib/motion";
import "./Prospectus.css";

/* ---------------------------------------------------------------------------
   The prospectus spread: four colleges on the left, the selected college's
   courses as a typeset ledger on the right. The ledger is the admission chart
   families already compare — course, duration, awarding body — so the columns
   carry information rather than decorating it.

   On narrow screens the two columns collapse into a single accordion: each
   college name becomes the header for its own ledger, which keeps the reading
   order intact instead of hiding the courses behind a control.
--------------------------------------------------------------------------- */

const Ledger = ({ college }) => (
  <ol className="ledger">
    {college.courses.map((course) => (
      <li className="ledger-row" key={course.id}>
        <span className="ledger-mark" aria-hidden="true">
          <FontAwesomeIcon icon={course.icon} />
        </span>
        <span className="ledger-course">
          <span className="ledger-code figures">{course.code}</span>
          <span className="ledger-name">{course.discipline}</span>
        </span>
        <span className="ledger-dur figures">{course.duration}</span>
        <span className="ledger-body">{course.awarding || course.fee}</span>
      </li>
    ))}
  </ol>
);

const Prospectus = () => {
  const [openSlug, setOpenSlug] = useState(colleges[0].slug);
  const [headRef, headShown] = useReveal();
  const active = colleges.find((c) => c.slug === openSlug) || colleges[0];

  return (
    <section className="section section--forest seam-top prospectus" id="colleges">
      <div className="container">
        <div ref={headRef} className={`reveal ${headShown ? "is-visible" : ""} prospectus-head`}>
          <div>
            <p className="eyebrow">Four colleges, one seal</p>
            <h2 className="h-lg">Find your college, then your course</h2>
          </div>
          <p className="lead">
            Every programme we run sits inside one of four colleges. Pick the
            one that matches where you're headed — the courses, their length
            and who awards the qualification are all listed.
          </p>
        </div>

        <div className="spread">
          {/* -------------------------------------------------- the index */}
          <div className="spread-index" role="tablist" aria-label="Colleges">
            {colleges.map((c) => {
              const isActive = c.slug === active.slug;
              return (
                <button
                  key={c.slug}
                  role="tab"
                  aria-selected={isActive}
                  aria-controls={`ledger-${c.slug}`}
                  className={`index-entry ${isActive ? "is-active" : ""}`}
                  style={{ "--tint": `var(--v-${c.slug}-lift)` }}
                  onClick={() => setOpenSlug(c.slug)}
                >
                  <span className="index-rule" aria-hidden="true" />
                  <span className="index-body">
                    <span className="index-name seal-type">{c.name}</span>
                    <span className="index-tagline">{c.tagline}</span>
                  </span>
                  <span className="index-count figures">
                    {c.courses.length}
                    <span className="sr-only"> courses</span>
                  </span>
                </button>
              );
            })}
          </div>

          {/* ------------------------------------------------- the ledger */}
          <div
            className="spread-sheet"
            id={`ledger-${active.slug}`}
            role="tabpanel"
            key={active.slug}
            style={{
              "--tint": `var(--v-${active.slug})`,
              "--tint-soft": `var(--v-${active.slug}-soft)`,
            }}
          >
            <div className="sheet-head">
              <h3 className="sheet-name seal-type">{active.name}</h3>
              <p className="sheet-lead">{active.lead}</p>
            </div>

            <div className="sheet-cols" aria-hidden="true">
              <span>Course</span>
              <span>Duration</span>
              <span>Awarded by</span>
            </div>

            <Ledger college={active} />

            <div className="sheet-foot">
              <a href={`/colleges/${active.slug}`} className="btn btn--primary">
                Explore {active.name}
                <FontAwesomeIcon icon={faArrowRight} />
              </a>
              <a href="/admissions" className="sheet-apply">
                Apply for admission
              </a>
            </div>
          </div>

          {/* ------------------------- mobile: one accordion, same content */}
          <div className="spread-stack">
            {colleges.map((c) => {
              const isOpen = c.slug === openSlug;
              return (
                <div
                  className={`stack-item ${isOpen ? "is-open" : ""}`}
                  key={c.slug}
                  style={{
                    "--tint": `var(--v-${c.slug})`,
                    "--tint-soft": `var(--v-${c.slug}-soft)`,
                  }}
                >
                  <button
                    className="stack-head"
                    aria-expanded={isOpen}
                    onClick={() => setOpenSlug(isOpen ? "" : c.slug)}
                  >
                    <span className="stack-name seal-type">{c.name}</span>
                    <span className="stack-count figures">{c.courses.length} courses</span>
                  </button>
                  {isOpen && (
                    <div className="stack-panel">
                      <p className="stack-lead">{c.lead}</p>
                      <Ledger college={c} />
                      <a href={`/colleges/${c.slug}`} className="btn btn--primary">
                        Explore {c.name}
                        <FontAwesomeIcon icon={faArrowRight} />
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Prospectus;
